import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { RiCloseLargeLine } from '@remixicon/react'
import { CloseButton, Content, Header, Overlay } from './mediaLightbox.styles'
import { MediaLightboxProps } from './mediaLightbox.types'
import { Media } from './components'

/**
 * MediaLightbox component to display a lightbox with different types of media.
 *
 * Usage rules:
 * - If `media.type === MediaLightboxType.IMAGE`, `media.src` must be the image URL.
 * - If `media.type === MediaLightboxType.IFRAME` and `media.provider === MediaLightboxProvider.YOUTUBE`, `media.src` must be the video ID.
 * - If `media.type === MediaLightboxType.IFRAME` and `media.provider === MediaLightboxProvider.HOTMART`, `media.src` must be the post ID.
 *
 * @param {MediaLightboxProps} props
 * @param {boolean} props.isOpen - Indicates whether the lightbox is open.
 * @param {MediaProps} props.media - The media properties to be displayed in the lightbox.
 * @param {() => void} props.onClose - Function called to close the lightbox.
 */
const MediaLightbox = ({ isOpen, media, onClose }: MediaLightboxProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      closeButtonRef.current?.focus()
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  return createPortal(
    <Overlay
      className="_position-fixed _w-full _h-full _d-flex _align-items-center _justify-content-center"
      isOpen={isOpen}
      tabIndex={-1}
      role="dialog"
      aria-hidden={!isOpen}
      aria-modal="true"
      onClick={onClose}
    >
      <Content
        className="_d-flex _align-items-center _justify-content-center _flex-column _position-relative _overflow-hidden _rounded-lg _shadow"
        onClick={(event) => event.stopPropagation()}
      >
        <Header className="_border-bottom _border-gray-300 _position-sticky _w-full _d-flex _justify-content-end">
          <CloseButton
            className="_cursor-pointer"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="close"
          >
            <RiCloseLargeLine size={24} />
          </CloseButton>
        </Header>

        <Media {...media} />
      </Content>
    </Overlay>,
    document.body
  )
}

export default MediaLightbox
